import { isResponseWithData } from 'lib-shared-auth';
import isEqual from 'lodash/isEqual';
import { useEffect, useState } from 'react';
import { usePrevious, useUpdateEffect } from 'react-use';

import { Template, templatesApi } from 'apis/templates';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { FlowNodeTypes } from '../components/types';
import { FlowAction } from '../types';

/**
 * A hook that fetches a list of templates based on the nodes available in the
 * flow.
 */
export const useTemplatesFetch = (
  actions: FlowAction[] | null,
): readonly Template[] => {
  const dispatch = useAppDispatch();
  const previousActions = usePrevious(actions);

  const [templateIds, setTemplateIds] = useState<string[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);

  // Handles collecting the list of template IDs from the actions.
  useEffect(() => {
    if (previousActions === actions) return;

    const nodeTemplateIds = getNodeTemplateIds(actions);
    const prevNodeTemplateIds = getNodeTemplateIds(previousActions);

    if (
      nodeTemplateIds.length &&
      !isEqual(nodeTemplateIds.sort(), prevNodeTemplateIds.sort())
    ) {
      setTemplateIds(nodeTemplateIds);
    }
  }, [actions, previousActions]);

  // Handles fetching the templates based on the collected template IDs.
  useUpdateEffect(() => {
    if (!templateIds.length) return;

    const fetchTemplates = async () => {
      const responses = await Promise.all(
        templateIds.map((id) =>
          dispatch(templatesApi.endpoints.readTemplateById.initiate({ id })),
        ),
      );

      const data = responses
        .filter(isResponseWithData)
        .map((response) => response.data!.data);

      setTemplates(data);
    };

    fetchTemplates();
  }, [templateIds]);

  return templates;
};

export function getNodeTemplateIds(actions?: FlowAction[] | null): string[] {
  if (!actions) return [];

  const set = actions.reduce<Set<string>>((acc, { nodeType, payload }) => {
    if (nodeType === FlowNodeTypes.AUTOREPLY) {
      acc.add(payload.auto_reply_template_id);
    }
    return acc;
  }, new Set<string>([]));

  return Array.from(set);
}
